"use client";

import React, { useEffect, useMemo, useRef, useState } from "react";
import { SearchFieldClient } from "../../atoms/search-field/search-field.client";
import { ChipRow } from "./chip-row";
import { ResetControl } from "./reset-control";
import { SearchStatus } from "./search-status";
import {
  CHANNEL_SEARCH_DEFAULTS,
  CHANNEL_SEARCH_MAX_SUGGESTIONS,
  CHANNEL_SEARCH_MIN_QUERY_LENGTH,
  type ChannelSearchItem,
  type ChannelSearchSelectionChange,
  channelSearchClasses,
  channelSearchFieldProps,
  channelSearchIds,
  resolveChannelSearchCopy
} from "./channel-search.shared";
import styles from "./channel-search.module.css";
import { lowercaseLabels, matchChannelSearchItems } from "./match-items";
import {
  addAllSelections,
  isAtSelectionLimit,
  selectedItemsOf,
  toggleSelection
} from "./selection";
import { SuggestionList } from "./suggestion-list";
import { useChipFocus } from "./use-chip-focus";
import { useChipOverflow } from "./use-chip-overflow";
import { useCombobox } from "./use-combobox";
import { withSafeRender } from "../../atoms/safe-render";

/**
 * ChannelSearchClient — SR1-SR12 and AX11: the field, the suggestion listbox,
 * the selection and the chips.
 *
 * ⚠ THE `"use client"` ARGUMENT, and it is this component's own. It implements
 * the ARIA Authoring Practices **combobox with a multi-selectable listbox
 * popup**, a pattern no native element provides: `<datalist>` carries no
 * selected state, no multi-select, no check marks and no controllable
 * highlight, and `<select multiple>` cannot be typed into. The selection half
 * has no zero-JavaScript form at all, which is why the server twin renders the
 * field alone rather than a degraded copy of this. That is DDS's accepted
 * argument #4, made here and not borrowed from another component.
 *
 * ⚠ ONE DIRECTIVE FOR THE WHOLE FOLDER. `chip-row`, `suggestion-list`,
 * `suggestion-row`, `use-combobox` and `use-chip-overflow` are imported only
 * from here, so they are already in the client graph; a directive of their own
 * would add nothing and would imply they are published exceptions, which they
 * are not. `channel-search.shared` stays directive-free because the SERVER
 * twin imports it too.
 *
 * ⚠ `onSelectionChange`, `onReset` and `onFieldActivate` are handler props on a
 * published component, and they pass all three tests: the component is already
 * across the boundary on an argument of its own, they carry data OUT of a
 * control that owns it, and none is a DOM event — `onFieldActivate` reports
 * that the reader put the search into USE and is handed no event object, which
 * is why it is not the `onClick` the third test bars.
 *
 * ⚠ CONTROLLED ON `selectedIds`, UNCONTROLLED ON THE TYPED STRING. A selection
 * is page state — it filters the grid (SR11) and reconciles against the ZIP —
 * so the caller owns it. The query is this component's own and is never
 * reported upward. (The atom beneath is driven in ITS controlled mode, which
 * is an implementation detail: it is what lets SR8's Reset blank the field.)
 *
 * ⛔ No fetch, no debounce, NO ANALYTICS — and the seam below is what keeps it
 * that way. `items` arrives already fetched and already ordered; every report is
 * the caller's, because this component knows the reader chose a row and not that
 * the row is a channel, nor what a channel click is called.
 */
export interface ChannelSearchClientProps {
  /** Unique on the page. Every id the component mints hangs off it. */
  id: string;
  /**
   * Everything the reader can find, already fetched and ALREADY ORDERED — the
   * component never sorts the source. Each item needs an opaque, caller-minted
   * `id`; see `ChannelSearchItem`.
   */
  items: readonly ChannelSearchItem[];
  /** ⛔ The chosen ids. Controlled: the component holds no selection state. */
  selectedIds: readonly string[];
  /**
   * Every add, remove, select-all and reset reports the whole next list, and
   * WHY it changed — see `ChannelSearchSelectionChange`. The two always arrive
   * together so a caller can never read one against a stale copy of the other.
   */
  onSelectionChange: (
    ids: string[],
    change: ChannelSearchSelectionChange
  ) => void;
  /**
   * The reader put the search into use — a click or tap landing on the field
   * itself, not on the ✕. Absent, nothing is reported. ⚠ Deliberately NOT a
   * focus signal: a tab-through on the way elsewhere is not the reader
   * searching, and this reports what the live page reports today.
   */
  onFieldActivate?: () => void;
  /** SR1's content-managed prompt, and the field's accessible name. */
  label?: string;
  /** SR2 — shown at exactly one character typed. */
  typingHint?: string;
  /** SR5 — the select-all row reads "{prefix} {typed text}". */
  selectAllPrefix?: string;
  /** SR5 — the heading a row with no `group` of its own sits under. */
  groupHeading?: string;
  /** SR9 — what the list says when nothing matched. */
  emptyMessage?: string;
  /**
   * SR10 — a selection limit. At it the field is disabled, further picks are
   * ignored and select-all fills only up to it; removing a chip re-enables the
   * field. Absent, there is no limit.
   */
  maxSelections?: number;
  /** SR7 — whether selections also show as removable chips. Defaults to on. */
  showChips?: boolean;
  /** SR7 — rows of chips before Show More appears, on desktop. */
  chipRowLimitDesktop?: number;
  /** SR7 — the same limit for tablet and mobile, which share a value; the
   *  switch to the desktop limit happens at 1280px, not the 735px tablet
   *  edge. */
  chipRowLimitMobile?: number;
  /** The verb in a chip's remove button, completed by the chip's own text. */
  chipRemoveLabel?: string;
  showMoreLabel?: string;
  showLessLabel?: string;
  /**
   * SR8 — present, a Reset control renders above the field and clears the text
   * AND every selection, and the ✕ then clears only the text. ABSENT, there is
   * no Reset control and the ✕ clears both.
   */
  resetLabel?: string;
  /** SR8 — fired after either of those two clearings. */
  onReset?: () => void;
  /** The accessible name of the ✕. Icon-only, so it needs one. */
  clearLabel?: string;
  /** Names the search landmark. Distinct from `label`, which names the input. */
  ariaLabel?: string;
  /** Caps the term length. Omitted, the browser accepts any length. */
  maxLength?: number;
  className?: string;
}

function ChannelSearchClientImpl({
  id,
  items,
  selectedIds,
  onSelectionChange,
  onFieldActivate,
  maxSelections,
  showChips = true,
  chipRowLimitDesktop = CHANNEL_SEARCH_DEFAULTS.chipRowLimitDesktop,
  chipRowLimitMobile = CHANNEL_SEARCH_DEFAULTS.chipRowLimitMobile,
  resetLabel,
  onReset,
  maxLength,
  className,
  ...authored
}: ChannelSearchClientProps) {
  const { fieldId, listboxId, statusId, chipsId } = channelSearchIds(id);
  const copy = resolveChannelSearchCopy(authored);

  const [query, setQuery] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [chipsExpanded, setChipsExpanded] = useState(false);

  const fieldWrapRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const chipListRef = useRef<HTMLUListElement>(null);
  const chipToggleRef = useRef<HTMLButtonElement>(null);

  const term = query.trim();
  /* Buys a lowercase pass over every label on each keystroke; `items` keeps
     its identity across typing, so this recomputes only on a new market. */
  const lowercased = useMemo(() => lowercaseLabels(items), [items]);
  const matches = matchChannelSearchItems(items, query, lowercased);
  /* The rows drawn and walked. `matches` stays whole for SR5's select-all and
     the live region's count. */
  const shown =
    matches.length > CHANNEL_SEARCH_MAX_SUGGESTIONS ?
      matches.slice(0, CHANNEL_SEARCH_MAX_SUGGESTIONS) :
      matches;
  const selectedItems = selectedItemsOf(items, selectedIds);
  const atLimit = isAtSelectionLimit(selectedIds, maxSelections);
  /* SR2's hint at one character, SR9's line when nothing matched. Either one
     REPLACES the options, so neither is ever shown beside a result. */
  const message =
    term.length === 0 ? undefined :
      term.length < CHANNEL_SEARCH_MIN_QUERY_LENGTH ? copy.typingHint :
        matches.length === 0 ? copy.emptyMessage :
          undefined;
  const hasList = isOpen && term.length > 0;

  /* SR2 vs SR12 — "the reader arrived" against "the component put focus back".
     Every return trip the component makes itself — Esc, a chosen row, SR8's
     Reset, AX11's chip removal — goes through `focusField`, and only those must
     leave the list closed. `focus()` dispatches synchronously, so the handler
     below reads the flag inside this call and the reset on the next line always
     wins; a field that already had focus fires no event and latches nothing. */
  const returningFocus = useRef(false);

  /* No ref is forwarded out of the atom, so the input is reached through the
     wrapper that holds it. It is the only input inside. */
  const focusField = () => {
    const input = fieldWrapRef.current?.querySelector("input");
    if (!input) {
      return;
    }
    returningFocus.current = true;
    input.focus();
    returningFocus.current = false;
  };

  const closeList = () => {
    setIsOpen(false);
    focusField();
  };

  /* SR5 at index 0, SR6 at 1..n. The one entry point the arrow walk and the
     pointer both go through, so they cannot drift — and the only place that
     still knows WHICH of the two the reader used, which is why the change is
     named here rather than inferred from the id lists downstream. */
  const activate = (index: number) => {
    if (index === 0) {
      onSelectionChange(
        addAllSelections(
          selectedIds,
          matches.map(match => match.item.id),
          maxSelections
        ),
        /* The row showed `{prefix} {term}`, so both halves travel and the
           caller decides how to join them. */
        {
          reason: "select-all",
          prefix: copy.selectAllPrefix,
          typedText: term
        }
      );
    } else {
      const { item } = shown[index - 1];
      const wasSelected = selectedIds.includes(item.id);
      const next = toggleSelection(selectedIds, item.id, maxSelections);
      /* SR10 — `undefined` is "ignored at the limit"; reporting it would fire
         a change event that changed nothing. */
      if (next) {
        onSelectionChange(next, {
          reason: wasSelected ? "deselect" : "select",
          item
        });
      }
    }
    closeList();
  };

  const walk = useCombobox({
    listboxId,
    isOpen: hasList,
    optionCount: message ? 0 : shown.length + 1,
    /* Keyed on the ids, so a new result set resets the highlight and a
       re-rendered identical one does not. */
    optionsKey: shown.map(match => match.item.id).join(" "),
    listRef,
    open: () => setIsOpen(true),
    close: closeList,
    activate
  });

  const { overflowing, clippedFrom } = useChipOverflow(
    chipListRef,
    chipRowLimitDesktop,
    chipRowLimitMobile,
    selectedItems.length
  );

  const focusChipAfterRemoval = useChipFocus(
    chipListRef,
    selectedItems.length,
    focusField
  );

  /* AX7 — the control keeps focus across its own toggle, but it leaves the DOM
     once the chips fit again and focus would then fall to <body>. */
  useEffect(() => {
    if (overflowing || !chipsExpanded) {
      return;
    }
    const hadFocus = document.activeElement === chipToggleRef.current;
    setChipsExpanded(false);
    if (hadFocus) {
      focusField();
    }
  }, [overflowing, chipsExpanded]);

  const clearSelections = () => {
    if (selectedIds.length > 0) {
      onSelectionChange([], { reason: "reset" });
    }
    onReset?.();
  };

  /* SR8's two clearings, told apart by where the click landed. The pill holds
     exactly ONE `<button>` — the ✕ — because the atom renders any caller mark
     inside `aria-hidden` and its contract forbids focusable slot content; a
     spec counts the buttons, so a second one fails there rather than here.
     Enter and Space on a button both raise a click, so this also covers AX1's
     keyboard activation of the ✕. */
  const handleFieldClick = (event: React.MouseEvent) => {
    const onClear =
      event.target instanceof Element && event.target.closest("button") !== null;
    /* SR2 — a tap on the ✕ counts as opening, so the list is up for the next
       characters typed. */
    setIsOpen(true);
    if (onClear) {
      if (!resetLabel) {
        clearSelections();
      }
      return;
    }
    /* The reader is starting a search rather than clearing one — the ✕ is a
       control of its own and reports as itself. */
    onFieldActivate?.();
  };

  return (
    <div
      className={channelSearchClasses(className)}
      onBlur={event => {
        /* SR12 — tabbing or clicking out of the search closes the list, and
           the typed text and selections are kept. A suggestion cancels its own
           pointer press, so choosing one never passes through here. */
        if (!event.currentTarget.contains(event.relatedTarget)) {
          setIsOpen(false);
        }
      }}
      onFocus={event => {
        /* SR2 — clicking, tapping or TABBING into the field opens the list.
           ⚠ WHO moved focus, not WHERE it came from. Testing `relatedTarget`
           for containment also swallowed SR8's Reset control, which sits inside
           this container and is the field's tab predecessor: the mouse reader
           got the list and the keyboard reader never did. `returningFocus` is
           set only by the component's own `focusField`, so a genuine arrival
           from any sibling opens and a return trip does not. */
        if ((event.target as HTMLElement).tagName !== "INPUT") {
          return;
        }
        if (returningFocus.current) {
          return;
        }
        setIsOpen(true);
      }}
      onKeyDown={walk.handleKeyDown}
    >
      {resetLabel ?
        <ResetControl
          label={resetLabel}
          onReset={() => {
            setQuery("");
            clearSelections();
            /* SR2 — "typing after Reset Search also opens it". The focus return
               on the next line is the component's own and deliberately does not
               open, and the term is now empty, so nothing shows until the next
               character is typed — which is exactly what the AC asks for. */
            setIsOpen(true);
            focusField();
          }}
        /> :
        null}

      <div className={styles.fieldWrap} ref={fieldWrapRef}>
        {/* The handler sits on the field alone, NOT on the positioning wrapper:
            a click on a suggestion bubbles, and from out here it would re-open
            the list that choosing the suggestion has just closed. The div is a
            delegation point for controls that carry their own keyboard
            handling, so the field inside stays operable by keyboard alone. */}
        <div onClick={handleFieldClick}>
          <SearchFieldClient
            {...channelSearchFieldProps({
              fieldId,
              label: copy.label,
              clearLabel: copy.clearLabel,
              ariaLabel: copy.ariaLabel,
              maxLength,
              // SR10 — dead at the limit, live again on a remove.
              disabled: atLimit
            })}
            ariaActiveDescendant={walk.activeOptionId}
            /* ⚠ LEFT UNCONDITIONAL, DELIBERATELY — same call `search-panel.tsx`
               makes at its own `ariaControls={listboxId}`, and for its stated
               reason, not a divergent one invented here: `aria-controls` names
               the combobox's popup by the APG pattern regardless of whether
               that popup is currently rendered, and `aria-expanded` is what
               tells assistive tech whether the named popup currently holds
               anything — gating `aria-controls` on `hasList` would also drop
               `role="combobox"`/`aria-expanded`/`aria-autocomplete` (see
               `SearchFieldClient`'s own comment), which is a bigger behaviour
               change than the id reference going stale while closed.
               `SuggestionList` is genuinely unmounted — not just hidden —
               while `hasList` is false, so `aria-controls` DOES point at an id
               with no element in the DOM in that state, and axe's
               `aria-valid-attr-value` rule will flag it. That is accepted, on
               the same APG-pattern basis `search-panel.tsx` already accepted it
               on: see `channel-search.client.spec.tsx`'s own test for this. */
            ariaControls={listboxId}
            ariaExpanded={hasList}
            onValueChange={setQuery}
            value={query}
          />
        </div>
        {hasList ?
          <SuggestionList
            activeIndex={walk.activeIndex}
            groupHeading={copy.groupHeading}
            listRef={listRef}
            listboxId={listboxId}
            matchCount={matches.length}
            matches={shown}
            message={message}
            onActivate={activate}
            selectAllLabel={`${copy.selectAllPrefix} ${term}`}
            selectedIds={selectedIds}
          /> :
          null}
      </div>

      {showChips && selectedItems.length > 0 ?
        <ChipRow
          clippedFrom={clippedFrom}
          expanded={chipsExpanded}
          id={chipsId}
          items={selectedItems}
          lessLabel={copy.showLessLabel}
          listRef={chipListRef}
          moreLabel={copy.showMoreLabel}
          onRemove={(removed, index) => {
            focusChipAfterRemoval(index);
            onSelectionChange(selectedIds.filter(each => each !== removed.id), {
              reason: "remove-chip",
              item: removed
            });
          }}
          onToggleExpanded={() => setChipsExpanded(expanded => !expanded)}
          overflowing={overflowing}
          removeLabel={copy.chipRemoveLabel}
          rowLimitDesktop={chipRowLimitDesktop}
          rowLimitMobile={chipRowLimitMobile}
          toggleRef={chipToggleRef}
        /> :
        null}

      <SearchStatus
        emptyMessage={copy.emptyMessage}
        id={statusId}
        matchCount={matches.length}
        query={query}
        typingHint={copy.typingHint}
      />
    </div>
  );
}
export const ChannelSearchClient = withSafeRender(ChannelSearchClientImpl, "ChannelSearchClient");

export default ChannelSearchClient;
